import { Injectable } from '@angular/core';

import {
  SyncExporter,
  ExporterDescriptor,
  ExportArtifact
} from '../models/exporter';
import {
  ExportPayload,
  ExportTaskRecord
} from '../models/export-payload';
import { AppConfigService } from '../../services/app-config.service';
import { extractExtras } from './extras-utils';

/**
 * Exporter Agent JSON (síncrono, 100% en cliente).
 *
 * Bundle "MCP-ready": estructura estática modelada siguiendo las convenciones
 * del Model Context Protocol consolidadas en 2026, de modo que cualquier
 * servidor MCP de terceros pueda ingerirlo como conjunto de resources, y
 * cualquier agente LLM lo entienda sin instrucciones adicionales.
 *
 * Decisiones de diseño (cerradas en el análisis del Punto 5):
 *   - URIs por entidad: estimation://{id} y estimation://{estId}/task/{taskId}.
 *     Cada tarea es referenciable de forma estable y única.
 *   - Provenance como { code, label }: forma canónica MCP de exponer enums
 *     (agente filtra por `code`, UI consume `label`).
 *   - manifest.agentHints: orienta a un LLM genérico sobre cómo interpretar
 *     el bundle sin tener que adivinar.
 *   - Sin $schema (todavía): el formato se autoidentifica con manifest.format +
 *     manifest.version. Cuando publiquemos un JSON Schema oficial, se añadirá.
 *   - Pretty-print con indentación 2 — los bundles típicos pesan 50-100 KB,
 *     no hay razón para minificar y la legibilidad humana es valor añadido.
 *   - Extensión .agent.json (convención emergente para JSON estructurado
 *     consumible por IA, distinta de un JSON genérico).
 */
@Injectable({ providedIn: 'root' })
export class AgentJsonExporter implements SyncExporter {

  readonly descriptor: ExporterDescriptor & { mode: 'sync' } = {
    id: 'agent-json',
    label: 'Agent JSON',
    shortHint: 'Bundle MCP-ready',
    // Material data_object icon ({} brackets) — visualmente "JSON estructurado".
    iconSvgPath:
      'M4 7v2c0 .55-.45 1-1 1H2v4h1c.55 0 1 .45 1 1v2c0 1.65 1.35 3 3 3h3v-2H7c-.55 0-1-.45-1-1v-2c0-1.3-.84-2.42-2-2.83v-.34C5.16 11.42 6 10.3 6 9V7c0-.55.45-1 1-1h3V4H7C5.35 4 4 5.35 4 7zm17 3c-.55 0-1-.45-1-1V7c0-1.65-1.35-3-3-3h-3v2h3c.55 0 1 .45 1 1v2c0 1.3.84 2.42 2 2.83v.34c-1.16.41-2 1.52-2 2.83v2c0 .55-.45 1-1 1h-3v2h3c1.65 0 3-1.35 3-3v-2c0-.55.45-1 1-1h1v-4h-1z',
    mode: 'sync',
    requiredPermission: 'estimation:export',
    supportsExtras: true,
    order: 30
  };

  constructor(private appConfig: AppConfigService) {}

  async generate(payload: ExportPayload, _options: unknown): Promise<ExportArtifact> {
    const bundle = this.buildBundle(payload);
    // JSON.stringify omite automáticamente claves con valor undefined —
    // forma idiomática 2026 de mantener el bundle sparse y limpio.
    const json = JSON.stringify(bundle, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });

    return {
      filename: this.buildFilename(payload),
      mimeType: 'application/json',
      blob
    };
  }

  // ==========================================================================
  // CONSTRUCCIÓN DEL BUNDLE
  // ==========================================================================

  private buildBundle(payload: ExportPayload): Record<string, unknown> {
    const estId = payload.estimation.id;
    const extrasIncluded = !!payload.extrasIncluded;

    return {
      manifest: {
        format: 'estimation-bundle',
        version: '1.0',
        mcpCompatible: true,
        extrasIncluded,
        generator: {
          productName: 'EstimatIA',
          productVersion: this.readProductVersion(),
          exportedAt: payload.estimation.exportedAt,
          exportedBy: payload.estimation.exportedBy
        },
        agentHints: {
          description:
            'Software project estimation produced via Retrieval-Augmented ' +
            'Generation. Each task carries a normalized provenance code ' +
            '(AI / HISTORIC / MANUAL with optional _MODIFIED suffix). Hours ' +
            'are P50; p10Hours and p90Hours provide the optimistic/pessimistic ' +
            'range when available.' +
            (extrasIncluded
              ? ' Tasks also include extended server-side fields: name, ' +
                'sourceSubtype, aiAssistanceFactor, calibrationFactorApplied, ' +
                'freshnessWeightApplied, analogProjectIds, specifics.'
              : ''),
          primaryEntity: 'task',
          totalsLocation: 'estimation.summary',
          provenanceField: 'provenance.code'
        }
      },
      estimation: {
        id: estId,
        uri: `estimation://${estId}`,
        name: payload.estimation.name,
        promptExcerpt: payload.estimation.promptExcerpt,
        generatedAt: payload.estimation.generatedAt,
        summary: payload.summary
      },
      tasks: payload.tasks.map(t => this.mapTask(estId, t, extrasIncluded))
    };
  }

  private mapTask(estId: string, t: ExportTaskRecord, extrasIncluded: boolean): Record<string, unknown> {
    const base: Record<string, unknown> = {
      uri: `estimation://${estId}/task/${t.id}`,
      id: t.id,
      requirement: {
        id: t.requirementId,
        description: t.requirementDescription
      },
      description: t.description,
      type: t.type,
      subsystem: t.subsystem,
      technology: t.technology,
      profile: t.profile,
      hours: t.hours,
      p10Hours: t.p10Hours,
      p90Hours: t.p90Hours,
      confidence: t.confidence,
      provenance: {
        code: t.provenance,
        label: t.provenanceLabel
      }
    };

    // Si el toggle "completo" está activo, spread los campos extras
    // (helper centralizado en extras-utils.ts).
    return extrasIncluded ? { ...base, ...extractExtras(t) } : base;
  }

  /**
   * Lee la versión del producto desde AppConfigService (clave 'Versión').
   * Defensivo: si la config no cargó o la clave no existe, devuelve 'unknown'
   * en lugar de romper la exportación. Mantiene la trazabilidad sin acoplar
   * el exporter al ciclo de vida del APP_INITIALIZER.
   */
  private readProductVersion(): string {
    const v = this.appConfig.get('Versión');
    return (typeof v === 'string' && v.length > 0) ? v : 'unknown';
  }

  // ==========================================================================
  // UTILS
  // ==========================================================================

  private buildFilename(payload: ExportPayload): string {
    const ts = this.timestampSlug(payload.estimation.exportedAt);
    const id = payload.estimation.id;
    return `estimacion-${id}-${ts}.agent.json`;
  }

  /** "2026-06-06T12:34:56.789Z" → "20260606-1234" (hora local). */
  private timestampSlug(iso: string): string {
    const d = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return (
      d.getFullYear().toString() +
      pad(d.getMonth() + 1) +
      pad(d.getDate()) +
      '-' +
      pad(d.getHours()) +
      pad(d.getMinutes())
    );
  }
}